import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Badge, Button, Col, Row } from "reactstrap"
import { CanView } from "components/Common/CanView"
import ButtonTooltip from "components/Common/ButtonTooltip"
import TableContainer from "components/Common/TableContainer"
import PageWrapper from "components/Common/PageWrapper"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import { useListParams } from "hooks/useListParams"
import {
  usePreOrders,
  useDeletePreOrder,
  useCancelPreOrder,
  useCreateMoneyReceiptFromPreOrder,
} from "hooks/usePreOrder"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"
import { downloadAndOpenFile } from "api/pdfApi"
import { useToastMessage } from "hooks/useToastMessage"
import { ACCESS } from "constants/accessPermissions"
import ConfirmModal from "components/Common/ConfirmModal"
import DeleteModal from "components/Common/DeleteModal"
import PrintDateModal from "pages/MoneyReceiptPages/PrintDateModal"
import GenerateMoneyReceiptModal from "pages/CustomerPages/CustomerProfile/PreOrder/GenerateMoneyReceiptModal"

const getBookingCustomerName = item =>
  item?.pdf_content?.customer_info?.name || item?.customer?.name || "-"

const getBookingCustomerMobile = item =>
  item?.pdf_content?.customer_info?.mobile || item?.customer?.mobile || "-"

const getBookingVehicleLabel = item =>
  item?.pdf_content?.vehicle_info?.name ||
  item?.vehicle?.stock_id ||
  item?.pdf_content?.vehicle_info?.stock_id ||
  "-"

const getBookingVehicleIdentity = item =>
  item?.pdf_content?.vehicle_info?.full_chassis ||
  item?.vehicle?.full_chassis ||
  item?.vehicle_description ||
  "No vehicle matched yet"

const PreOrderListPage = () => {
  document.title = "Pre-Order List | Aurobil Cars"

  const navigate = useNavigate()
  const location = useLocation()
  const { handleResponse, handleError } = useToastMessage()
  const {
    params,
    setPage,
    setSearch,
    updateParams,
    resetParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
  })

  const focusId = params?.pb_focus ?? null
  const focusRowRef = useRef(null)

  const [printDateUrl, setPrintDateUrl] = useState(null)
  const [printDateDefault, setPrintDateDefault] = useState("")
  const [isPrinting, setIsPrinting] = useState(false)
  const [deleteModal, setDeleteModal] = useState(null)
  const [cancelModal, setCancelModal] = useState(null)
  const [receiptModal, setReceiptModal] = useState(null)

  const openPrintDateModal = (url, defaultDate = "") => {
    setPrintDateDefault(defaultDate || "")
    setPrintDateUrl(url)
  }

  const handlePrintDateConfirm = async printDate => {
    const url = printDateUrl
    setPrintDateUrl(null)
    if (!url) return
    setIsPrinting(true)
    try {
      await downloadAndOpenFile({ url, params: { print_date: printDate || "" } })
    } catch (error) {
      handleError(error)
    } finally {
      setIsPrinting(false)
    }
  }

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: params.per_page ?? 10,
      search: params.search ?? "",
    }),
    [params]
  )

  const { data, isLoading, refetch, isRefetching } = usePreOrders(queryParams)
  const { mutateAsync: deletePreOrder, isPending: isDeleting } =
    useDeletePreOrder()
  const { mutateAsync: cancelPreOrder, isPending: isCancelling } =
    useCancelPreOrder()
  const { mutateAsync: createMoneyReceipt, isPending: creatingReceipt } =
    useCreateMoneyReceiptFromPreOrder()

  const fetchedData = data?.data?.data ?? []
  const paginationInfo = data?.data

  useEffect(() => {
    if (!focusId) return
    const node = focusRowRef.current
    if (!node) return
    node.scrollIntoView({ behavior: "smooth", block: "center" })
    node.classList.add("table-warning")
    const timer = setTimeout(() => {
      node.classList.remove("table-warning")
      updateParams({ pb_focus: undefined })
    }, 2500)
    return () => clearTimeout(timer)
  }, [focusId, fetchedData, updateParams])

  const handleDelete = async item => {
    try {
      const response = await deletePreOrder({ id: item.id })
      handleResponse(response, "Pre-order deleted successfully.", true)
      setDeleteModal(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to delete pre-order.")
    }
  }

  const handleCancel = async item => {
    try {
      const response = await cancelPreOrder(item.id)
      handleResponse(response, "Pre-order cancelled successfully.", true)
      setCancelModal(null)
      refetch()
    } catch (error) {
      handleError(error, "Failed to cancel pre-order.")
    }
  }

  const handleGenerateReceipt = async payload => {
    try {
      const response = await createMoneyReceipt(payload)
      handleResponse(response, "Money receipt generated successfully.", true)
      refetch()
      const moneyReceiptId = response?.data?.id
      if (moneyReceiptId) {
        navigate(`/money-receipts/${moneyReceiptId}`)
      }
    } catch (error) {
      handleError(error, "Failed to generate money receipt.")
      throw error
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Booking",
        cell: ({ row }) => {
          const item = row.original
          return (
            <div>
              <div className="fw-bold text-primary">{item?.poid ?? "-"}</div>
              <div className="small text-muted">
                Created: {formatReadableDate(item?.created_at, "MMM d, y")}
              </div>
              <div className="d-sm-none mt-1">
                <div className="small text-muted">
                  {getBookingCustomerName(item)}
                </div>
                <div className="small text-muted">
                  {getBookingCustomerMobile(item)}
                </div>
                <div className="mt-1">
                  <Badge
                    color={item?.status === "cancelled" ? "danger" : "success"}
                  >
                    {item?.status === "cancelled" ? "Cancelled" : "Booked"}
                  </Badge>
                </div>
              </div>
            </div>
          )
        },
      },
      {
        header: "Customer",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          const customerIdentifier =
            item?.customer?.cuid ?? item?.customer?.id
          return (
            <div>
              {customerIdentifier ? (
                <Link
                  to={`/customer-list/${customerIdentifier}/view?tab=pre-orders`}
                  className="fw-semibold"
                >
                  {getBookingCustomerName(item)}
                </Link>
              ) : (
                <div className="fw-semibold">{getBookingCustomerName(item)}</div>
              )}
              <div className="small text-muted">
                {getBookingCustomerMobile(item)}
              </div>
            </div>
          )
        },
      },
      {
        header: "Vehicle",
        meta: { className: "d-none d-md-table-cell" },
        cell: ({ row }) => {
          const item = row.original
          const stockId = item?.vehicle?.stock_id
          return (
            <div>
              {stockId ? (
                <Link to={`/stock-list/${stockId}/view?tab=pre-orders`}>
                  {getBookingVehicleLabel(item)}
                </Link>
              ) : (
                <div>{getBookingVehicleLabel(item)}</div>
              )}
              <div className="small text-muted">
                {getBookingVehicleIdentity(item)}
              </div>
            </div>
          )
        },
      },
      {
        header: "Deposit",
        meta: { className: "d-none d-lg-table-cell" },
        cell: ({ row }) => (
          <div className="fw-bold text-success">
            {formatBDT(row.original?.deposit_amount)}
          </div>
        ),
      },
      {
        header: "Status",
        meta: { className: "d-none d-sm-table-cell" },
        cell: ({ row }) => (
          <Badge
            color={row.original?.status === "cancelled" ? "danger" : "success"}
          >
            {row.original?.status === "cancelled" ? "Cancelled" : "Booked"}
          </Badge>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const item = row.original
          const canEdit = item?.status !== "cancelled"
          const canCancel = item?.status !== "cancelled"
          const canGenerateReceipt =
            Boolean(item?.vehicle_id) && item?.status !== "cancelled"

          return (
            <ul className="list-unstyled hstack gap-1 mb-0 flex-wrap">
              <CanView access={[ACCESS.PRE_ORDER.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="view"
                    btnId={`pb-view-${item.id}`}
                    onClick={() =>
                      navigate(`/pre-orders/${item.id}`, {
                        state: { from: location },
                      })
                    }
                  />
                </li>
              </CanView>
              <CanView access={[ACCESS.PRE_ORDER.EDIT]}>
                <li>
                  <ButtonTooltip
                    type="edit"
                    color="warning"
                    btnId={`pb-edit-${item.id}`}
                    disabled={!canEdit}
                    onClick={() =>
                      navigate(`/pre-orders/${item.id}/edit`, {
                        state: { from: location },
                      })
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.PRE_ORDER.VIEW]}>
                <li>
                  <ButtonTooltip
                    type="pdf"
                    color="info"
                    btnId={`pb-pdf-${item.id}`}
                    onClick={() =>
                      openPrintDateModal(
                        `/pre-orders/${item.id}/pdf`,
                        item.created_at
                      )
                    }
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.PRE_ORDER.CREATE_MONEY_RECEIPT]}>
                <li>
                  <ButtonTooltip
                    type="receipt"
                    btnId={`pb-mr-${item.id}`}
                    btnName={
                      canGenerateReceipt
                        ? "Generate Money Receipt"
                        : "Add chassis/engine number first"
                    }
                    loading={creatingReceipt}
                    disabled={!canGenerateReceipt}
                    onClick={() => setReceiptModal(item)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.PRE_ORDER.CANCEL]}>
                <li>
                  <ButtonTooltip
                    type="cancel"
                    btnId={`pb-cancel-${item.id}`}
                    disabled={!canCancel || isCancelling}
                    onClick={() => setCancelModal(item)}
                  />
                </li>
              </CanView>

              <CanView access={[ACCESS.PRE_ORDER.DELETE]}>
                <li>
                  <ButtonTooltip
                    type="delete"
                    btnId={`pb-delete-${item.id}`}
                    disabled={isDeleting}
                    onClick={() => setDeleteModal(item)}
                  />
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [navigate, location, isDeleting, isCancelling, creatingReceipt]
  )

  return (
    <PageWrapper title="Pre-Order" breadcrumbItem="Pre-Order List">
      <Row className="mt-3 gy-2 align-items-center">
        <Col xs={6}>
          <h5 className="mb-0 card-title">
            Total{" "}
            <span className="badge badge-soft-info">
              {paginationInfo?.total ?? 0}
            </span>
          </h5>
        </Col>
        <Col xs={6} className="d-flex justify-content-end align-items-center gap-1">
          <ButtonTooltip
            type="refresh"
            btnId="refreshPreOrderList"
            size={null}
            loading={isRefetching}
            onClick={() => refetch()}
          />
          <CanView access={[ACCESS.PRE_ORDER.CREATE]}>
            <Button color="primary" onClick={() => navigate("/pre-orders/create")}>
              <i className="bx bx-plus" />
              <span className="d-none d-sm-inline ms-1">Add Pre-Order</span>
            </Button>
          </CanView>
          {hasActiveFilters() && (
            <Button color="danger" onClick={() => resetParams([], { page: 1 })}>
              <span className="d-none d-sm-inline">Reset Filters</span>
              <i className="d-sm-none mdi mdi-filter-remove-outline" />
            </Button>
          )}
        </Col>
      </Row>

      <Row className="my-3">
        <Col xs={12} sm={6} md={4}>
          <GlobalInputSearch
            value={params.search ?? ""}
            onChange={setSearch}
            placeholder="Search booking no, customer, vehicle..."
          />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={fetchedData}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading}
        tableClass="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-4 border-top"
        customPageSize={queryParams.per_page}
        getRowKey={row => row.id}
        rowProps={row => {
          const isFocus =
            focusId &&
            (String(row.id) === String(focusId) ||
              String(row.poid) === String(focusId))
          if (!isFocus) return {}
          return { ref: focusRowRef }
        }}
      />

      <PrintDateModal
        isOpen={Boolean(printDateUrl)}
        toggle={() => setPrintDateUrl(null)}
        onSubmit={handlePrintDateConfirm}
        isSubmitting={isPrinting}
        title="Pre-Order — Print Date"
        defaultDate={printDateDefault}
      />

      {receiptModal ? (
        <GenerateMoneyReceiptModal
          isOpen={!!receiptModal}
          toggle={() => setReceiptModal(null)}
          preOrder={receiptModal}
          onSubmit={handleGenerateReceipt}
          isSubmitting={creatingReceipt}
        />
      ) : null}

      <DeleteModal
        show={!!deleteModal}
        onDeleteClick={() => handleDelete(deleteModal)}
        onCloseClick={() => setDeleteModal(null)}
        loading={isDeleting}
      />

      <ConfirmModal
        isOpen={!!cancelModal}
        toggle={() => setCancelModal(null)}
        title="Cancel Pre-Order?"
        message="This will cancel the pre-order and release the reserved vehicle if no other active booking holds it. Do you want to proceed?"
        confirmLabel="Cancel Booking"
        icon="mdi-close-circle-outline"
        btnColor="warning"
        onConfirm={() => handleCancel(cancelModal)}
        isConfirming={isCancelling}
      />
    </PageWrapper>
  )
}

export default PreOrderListPage
